/**
 * Environment Configuration
 * Contains all environment-specific settings for the application
 */

import { getEnvironmentTier } from '../utils/environment';

export interface EnvironmentConfig {
  paddle: {
    clientToken: string;
    vendorId: string; // Legacy, kept for reference
  };
  stripe?: {
    publicKey: string;
    checkoutBaseUrl: string;
  };
  centrobill?: {
    publicKey: string;
    checkoutBaseUrl: string;
  };
  amplitude?: {
    apiKey: string;
    serverZone: 'US' | 'EU';
  };
  googleTag?: {
    measurementId: string;
  };
  metaPixel?: {
    pixelId: string;
  };
}

const CONFIGS: Record<'sandbox' | 'production', EnvironmentConfig> = {
  sandbox: {
    paddle: {
      // Sandbox client-side token for Paddle.js SDK
      clientToken: 'test_631f1a58e618e364df1c38e5a8d',
      vendorId: '39188' // Legacy vendor ID
    },
    stripe: {
      publicKey: 'pk_test_51SKDbyIczEZlgnndisYk3h9NWSgJC5ddzsCFGfgi7jE8vv3eQzuSitNlIOrKnkOlw3cDqVx9W540DnlPlN9u4csf00G0Pyhxic',
      checkoutBaseUrl: 'https://api-sandbox.lunai.care/stripe'
    },
    centrobill: {
      publicKey: 'HVBUMuwX3nwRPYG',
      checkoutBaseUrl: 'https://api-sandbox.lunai.care/centrobill'
    },
    amplitude: {
      // Amplitude SANDBOX project API key (client-safe write key; same value as the
      // worker's AMPLITUDE_API_KEY sandbox secret). Source of truth: lunai.care vault.
      // NOTE: the Amplitude org lives in the EU data zone — keys are only valid
      // against api.eu.amplitude.com (serverZone 'EU').
      apiKey: 'e23c7d09e24a19a1847fa6584b089438',
      serverZone: 'EU'
    },
    googleTag: {
      // Google Tag ID (client-safe). GT- tag: its destinations (GA4 app property,
      // Google Ads) are configured in the Google Tag UI (tagmanager.google.com),
      // not here. Shared with production — sandbox hits carry debug_mode (visible
      // in DebugView); to keep them out of standard reports, activate the
      // "Developer traffic" data filter in the destination GA4 property, or split
      // into a dedicated sandbox tag later.
      measurementId: 'GT-MBNK8L3J'
    },
    metaPixel: {
      // Sandbox Meta Pixel/Dataset ID ("Lunai Web Sandbox"). Do NOT point
      // sandbox at the production pixel — it would pollute real conversion data.
      pixelId: '1685126632739535'
    }
  },
  production: {
    paddle: {
      // Production client-side token for Paddle.js SDK
      clientToken: 'live_5916b21c1fff3e36098c9a75c90',
      vendorId: '258684' // Legacy vendor ID
    },
    stripe: {
      publicKey: 'pk_live_51SKDbqRHASfRRDD8FQNHaXEpejHWaSqUy5QOtDY1kmVF9BZKMVndg9vRmiACiQ37qKURFp1VYAm2OL1M48xUfZ2X00AtSCZeUb',
      checkoutBaseUrl: 'https://api.lunai.care/stripe'
    },
    centrobill: {
      publicKey: 'HVBUMuwX3nwRPYG',
      checkoutBaseUrl: 'https://api.lunai.care/centrobill'
    },
    amplitude: {
      // Amplitude PRODUCTION project API key (client-safe write key; same value as the
      // worker's AMPLITUDE_API_KEY production secret). Source of truth: lunai.care vault.
      // NOTE: EU data zone (see sandbox note).
      apiKey: '03d33db5688db69df0edcf6e39917aa1',
      serverZone: 'EU'
    },
    googleTag: {
      // Google Tag ID (client-safe). GT- tag → destinations (GA4 app property,
      // Google Ads) are configured in the Google Tag UI, not in code.
      measurementId: 'GT-MBNK8L3J'
    },
    metaPixel: {
      // Meta Pixel/Dataset ID for start.lunai.care (client-safe). Must match
      // the worker's META_PIXEL_ID so browser/server Purchase events dedup by
      // shared event_id.
      pixelId: '1378677031027719'
    }
  }
};

/**
 * Get the configuration for the current environment
 */
export function getEnvConfig(): EnvironmentConfig {
  const tier = getEnvironmentTier();
  return CONFIGS[tier];
}

/**
 * Get Paddle client token for current environment
 */
export function getPaddleClientToken(): string {
  return getEnvConfig().paddle.clientToken;
}

/**
 * Get Stripe public key for current environment
 */
export function getStripePublicKey(): string | undefined {
  return getEnvConfig().stripe?.publicKey;
}

/**
 * Get Stripe checkout base URL for current environment
 */
export function getStripeCheckoutBaseUrl(): string | undefined {
  return getEnvConfig().stripe?.checkoutBaseUrl;
}

/**
 * Get Centrobill public key for current environment
 */
export function getCentrobillPublicKey(): string | undefined {
  return getEnvConfig().centrobill?.publicKey;
}

/**
 * Get Centrobill checkout base URL for current environment
 */
export function getCentrobillCheckoutBaseUrl(): string | undefined {
  return getEnvConfig().centrobill?.checkoutBaseUrl;
}

/**
 * Get Amplitude config (apiKey + serverZone) for current environment
 */
export function getAmplitudeConfig(): EnvironmentConfig['amplitude'] {
  return getEnvConfig().amplitude;
}

/**
 * Get Google tag (GA4) config for current environment
 */
export function getGoogleTagConfig(): EnvironmentConfig['googleTag'] {
  return getEnvConfig().googleTag;
}

/**
 * Get Meta (Facebook) Pixel ID for current environment.
 * Empty string means the pixel is disabled for this tier.
 */
export function getMetaPixelId(): string {
  return getEnvConfig().metaPixel?.pixelId || '';
}